import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Alert, Button, Space, Spin, Switch, Tooltip} from 'antd';
import {useNavigate} from 'react-router-dom';
import {KeyOutlined, RedoOutlined, SyncOutlined} from '@ant-design/icons';
import BalanceCards, {BalanceCardsRef} from '../../components/trading/BalanceCards';
import {OkxPositionService} from '../../services/okxPositionService';
import type {OkxPositionSummary} from '../../types/okxPosition';
import {usePageTimer} from '../../hooks/usePageTimer';
import './TradingDashboard.css';

const TradingDashboard: React.FC = () => {
    const navigate = useNavigate();
    const [positionSummary, setPositionSummary] = useState<OkxPositionSummary | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>('');
    const [selectedCex, setSelectedCex] = useState<string>('ALL');
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [hasApiConnection, setHasApiConnection] = useState<boolean | null>(null);
    const balanceCardsRef = useRef<BalanceCardsRef>(null);

    // forceRefresh=true 时强制后端回源交易所（跳过缓存），供手动刷新使用
    const fetchPositionData = async (forceRefresh = false) => {
        setLoading(true);
        setError('');
        try {
            const positions = await OkxPositionService.getLatestPositions({
                vendor: selectedCex,
                refresh: forceRefresh
            });
            const summary = OkxPositionService.calculatePositionSummary(positions);
            setPositionSummary(summary);
            setHasApiConnection(true);
        } catch (error: any) {
            const errorMsg = error?.message || '';
            if (errorMsg.includes('401') || errorMsg.includes('Unauthorized') ||
                errorMsg.includes('API') || errorMsg.includes('连接')) {
                setHasApiConnection(false);
                setError('');
            } else {
                setHasApiConnection(null);
                setError('获取持仓数据失败');
            }
        } finally {
            setLoading(false);
        }
    };

    const {isActive: isTimerActive, start: startTimer, stop: stopTimer} = usePageTimer(
        async () => {
            try {
                await Promise.all([
                    fetchPositionData(),
                    balanceCardsRef.current?.refreshBalance()
                ]);
            } catch (error) {
                // console.error('定时器刷新失败:', error);
            }
        },
        {
            interval: 10000,
            persistKey: 'trading-dashboard-timer',
            autoStart: false
        }
    );

    const toggleAutoRefresh = useCallback((enabled: boolean) => {
        if (enabled) {
            startTimer();
        } else {
            stopTimer();
        }
    }, [startTimer, stopTimer]);

    const handleManualRefresh = useCallback(async () => {
        if (isRefreshing) return;
        setIsRefreshing(true);

        try {
            await Promise.all([
                fetchPositionData(true),
                balanceCardsRef.current?.refreshBalance()
            ]);
        } catch (error) {
            // console.error('手动刷新失败:', error);
        } finally {
            setIsRefreshing(false);
        }
    }, [isRefreshing]);

    useEffect(() => {
        fetchPositionData();
    }, []);

    useEffect(() => {
        if (selectedCex) {
            fetchPositionData();
        }
    }, [selectedCex]);

    const totalUpl = positionSummary?.totalUpl || 0;
    const totalMargin = positionSummary?.totalMargin || 0;
    const isPositive = totalUpl >= 0;
    const uplPercent = totalMargin > 0 ? (totalUpl / totalMargin) * 100 : 0;

    return (
        <div className="trading-dashboard">
            {/* 控制栏 */}
            <div className="control-bar" style={{justifyContent: 'flex-end'}}>
                <Space split={<span style={{color: 'var(--pt-border)', margin: '0 4px'}}>|</span>}>
                    <Space>
                        <Tooltip title={isTimerActive ? '自动刷新已开启' : '自动刷新已关闭'}>
                            <SyncOutlined
                                className={`refresh-icon ${isRefreshing ? 'active' : ''}`}
                                style={{
                                    color: isTimerActive ? 'var(--pt-bull)' : 'var(--pt-text-secondary)',
                                    fontSize: 14
                                }}
                            />
                        </Tooltip>
                        <Switch
                            checked={isTimerActive}
                            onChange={() => toggleAutoRefresh(!isTimerActive)}
                            size="small"
                        />
                    </Space>
                    <Tooltip title="手动刷新">
                        <RedoOutlined
                            spin={isRefreshing}
                            className={isTimerActive || isRefreshing ? '' : 'cursor-pointer'}
                            onClick={() => {
                                if (!isTimerActive && !isRefreshing) {
                                    handleManualRefresh();
                                }
                            }}
                            style={{
                                color: isTimerActive || isRefreshing ? 'var(--pt-text-secondary)' : 'var(--pt-primary)',
                                pointerEvents: isTimerActive || isRefreshing ? 'none' : 'auto',
                                cursor: isTimerActive || isRefreshing ? 'not-allowed' : 'pointer',
                                fontSize: 14
                            }}
                        />
                    </Tooltip>
                </Space>
            </div>

            {/* KPI 卡片 */}
            <div className="kpi-grid">
                <div className="kpi-card glow-on-hover">
                    <div className="kpi-label">总持仓</div>
                    <Spin spinning={loading || isRefreshing}>
                        <div className="kpi-value text-gold">
                            {(positionSummary?.longPositions || 0) + (positionSummary?.shortPositions || 0)}
                            <span className="unit">笔</span>
                        </div>
                        <div className="kpi-change">
                            <span>多 {(positionSummary?.longPositions || 0)}</span>
                            <span style={{margin: '0 8px', color: 'var(--pt-border)'}}>/</span>
                            <span>空 {(positionSummary?.shortPositions || 0)}</span>
                        </div>
                    </Spin>
                </div>

                <div className="kpi-card glow-on-hover">
                    <div className="kpi-label">未结盈亏</div>
                    <Spin spinning={loading || isRefreshing}>
                        <div className={`kpi-value ${isPositive ? 'text-teal' : 'text-coral'}`}>
                            {isPositive ? '+' : ''}
                            {OkxPositionService.formatNumber(positionSummary?.totalUpl || 0)}
                            <span className="unit">USDT</span>
                        </div>
                        <div className="kpi-change">
                            <span className={isPositive ? 'up' : 'down'}>
                                {isPositive ? '▲' : '▼'} {Math.abs(uplPercent).toFixed(2)}%
                            </span>
                            <span style={{marginLeft: '8px'}}>收益率</span>
                        </div>
                    </Spin>
                </div>

                <div className="kpi-card glow-on-hover">
                    <div className="kpi-label">保证金使用</div>
                    <Spin spinning={loading || isRefreshing}>
                        <div className="kpi-value" style={{color: 'var(--pt-amber)'}}>
                            {OkxPositionService.formatPercentage((positionSummary?.totalMargin || 0) / 100)}
                        </div>
                        <div className="kpi-change">
                            <span>保证金: {OkxPositionService.formatNumber(positionSummary?.totalMargin || 0)} USDT</span>
                        </div>
                    </Spin>
                </div>

                <div className="kpi-card glow-on-hover">
                    <div className="kpi-label">风险状态</div>
                    <Spin spinning={loading || isRefreshing}>
                        <div className="kpi-value text-teal">安全</div>
                        <div className="kpi-change">
                            <span>未触及止损线</span>
                        </div>
                    </Spin>
                </div>
            </div>

            {/* 账户余额统计 */}
            <div className="balance-section">
                <BalanceCards
                    ref={balanceCardsRef}
                    selectedCex={selectedCex}
                    onCexChange={setSelectedCex}
                />
            </div>


            {/* API未配置提示 */}
            {hasApiConnection === false && (
                <Alert
                    message="尚未配置API连接"
                    description="请先配置API连接，以便获取持仓数据。"
                    type="info"
                    showIcon
                    action={
                        <Button size="small" type="primary" onClick={() => navigate('/api-connection')}>
                            <KeyOutlined/> 去配置
                        </Button>
                    }
                    style={{marginBottom: 16}}
                />
            )}

            {/* 错误提示 */}
            {error && hasApiConnection !== false && (
                <Alert
                    message="错误"
                    description={error}
                    type="error"
                    showIcon
                    action={
                        <span
                            className="cursor-pointer text-blue-600"
                            onClick={() => fetchPositionData(true)}
                        >
                            重试
                        </span>
                    }
                    style={{marginBottom: 16}}
                />
            )}
        </div>
    );
};

export default TradingDashboard;
